import React, { useState } from 'react';
import { Box, ButtonBase, Collapse, Tooltip, Typography } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { HILL_COLORS } from './hillGeometry';

const ITEMS = [
  { label: 'Figuring things out', color: HILL_COLORS.figuring },
  { label: 'Making it happen', color: HILL_COLORS.making },
  { label: 'Done', color: HILL_COLORS.done },
  { label: 'Not started', color: HILL_COLORS.notStarted },
];

/**
 * How much a dot's position is worth, which is a different axis from where it
 * sits. Only a person can say whether the unknowns in a piece of work are
 * resolved, so a dot they placed is drawn solid and everything Jira filled in for
 * them is drawn fainter.
 */
const CONFIDENCE = [
  {
    label: 'Someone placed it',
    hint: 'A person dragged this dot, so its position is a judgement about the unknowns.',
    sx: { bgcolor: 'text.primary' },
  },
  {
    label: 'From the Jira status',
    hint: 'Positioned by the status map. Nobody has confirmed it, so it reports the workflow rather than the unknowns.',
    sx: { bgcolor: 'text.primary', opacity: 0.5 },
  },
  {
    label: 'Guessed',
    hint: 'This Jira status is not in the hill map, so the position came from Jira\'s status category and is held short of the peak.',
    sx: { border: '1.5px dashed', borderColor: 'text.secondary' },
  },
];

const LegendItems = () => (
  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'nowrap' }}>
    {ITEMS.map(item => (
      <Box key={item.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: item.color, flexShrink: 0 }} />
        <Typography variant="caption" color="text.secondary" noWrap>{item.label}</Typography>
      </Box>
    ))}
    {CONFIDENCE.map(item => (
      <Tooltip key={item.label} title={item.hint}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Box sx={{ width: 10, height: 10, borderRadius: '50%', flexShrink: 0, ...item.sx }} />
          <Typography variant="caption" color="text.secondary" noWrap>{item.label}</Typography>
        </Box>
      </Tooltip>
    ))}
  </Box>
);

/**
 * The colour key is noise during day-to-day use, so it stays tucked behind a
 * "Legend" toggle and slides out on demand.
 */
const HillLegend = () => {
  const [open, setOpen] = useState(false);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
      <Collapse in={open} orientation="horizontal" timeout={200}>
        <Box sx={{ pr: 1.5 }}>
          <LegendItems />
        </Box>
      </Collapse>
      <ButtonBase
        onClick={() => setOpen(current => !current)}
        aria-expanded={open}
        aria-label={open ? 'Hide status legend' : 'Show status legend'}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.25,
          flexShrink: 0,
          borderRadius: 1,
          px: 0.5,
          py: 0.25,
          color: 'text.secondary',
          '&:hover': { color: 'text.primary' },
        }}
      >
        <ChevronLeftIcon
          sx={{
            fontSize: 16,
            transition: 'transform .2s',
            transform: open ? 'rotate(180deg)' : 'none',
          }}
        />
        <Typography variant="caption" sx={{ fontWeight: 500 }}>
          Legend
        </Typography>
      </ButtonBase>
    </Box>
  );
};

export default HillLegend;
